// Coldroom -> Cold treatment (USA) tab (R-A6, PPECB HP05 rev 9).
// Start a lot (room, pallets, protocol), place at least 3 probes, read the pulp hourly, and watch the
// lot become Ready. Ready is computed by the server (cold_treatment_status) and says why it is not.
// Readings are append-only: a mistake is a correction that supersedes the old reading.
import { useMemo, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQueries, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import { AlertTriangle, CheckCircle2, Plus, Printer, Snowflake } from 'lucide-react'
import clsx from 'clsx'
import PageHeader from '@/components/PageHeader'
import QueryStateGate from '@/components/QueryStateGate'
import SubmitOverlay from '@/components/SubmitOverlay'
import { COLD_TREATMENT_DOMAIN } from '@/lib/packhouseErrorDomains'
import { Button } from '@/components/ui'
import { supabase } from '@/lib/supabase'
import { withTimeout } from '@/lib/saveTimeout'
import { sastHM, sastYMD } from '@/lib/dates'
import { readState } from '@/lib/readState'
import { usePermissions } from '@/lib/usePermissions'
import { useUrlState } from '@/hooks/useUrlState'
import { useRecorderNames } from '@/modules/packhouse/ColdroomTemperatures'
import { sanitizeTempInput, supersededIds } from '@/lib/coldroomTemps'
import {
  fmtC, parsePulp, type ColdTreatmentLot, type ColdTreatmentProbe, type ColdTreatmentProbeStatus,
  type ColdTreatmentReading, type ColdTreatmentStatus,
} from '@/lib/coldTreatment'
import {
  CT_KEYS, fetchCtLots, fetchCtRooms, fetchCtStatus, useCtLotDetail, useCtProbeLabels, useCtStatus, type CtRoom, type LotDetail,
} from './coldTreatmentData'
import {
  AddPalletsModal, CorrectReadingModal, PlaceProbeModal, RetireProbeModal, StartLotModal,
  type PlaceProbeVars, type StartLotVars,
} from './ColdTreatmentForms'

const stamp = (iso: string) => `${sastYMD(Date.parse(iso))} ${sastHM(iso) ?? ''}`.trim()

// ---------------------------------------------------------------- writes
type Job = () => Promise<void>
function useCtWrite(onDone?: () => void) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (job: Job) => job(),
    onSuccess: () => {
      onDone?.()
      void qc.invalidateQueries({ queryKey: ['ct-lots'] })
      void qc.invalidateQueries({ queryKey: ['ct-lot'] })
      void qc.invalidateQueries({ queryKey: ['ct-status'] })
      void qc.invalidateQueries({ queryKey: ['ct-room-pallets'] })
    },
  })
}

async function startLot(v: StartLotVars): Promise<string> {
  const { data, error } = await withTimeout(supabase.from('cold_treatment_lots').insert({
    coldroom_id: v.coldroomId, threshold_c: v.threshold, required_hours: v.requiredHours, final_hours: v.finalHours, note: v.note,
  }).select('lot_id').single(), 'ColdTreatment: lot insert')
  if (error) throw error
  const lotId = (data as { lot_id: string }).lot_id
  const { error: e2 } = await withTimeout(supabase.from('cold_treatment_lot_pallets')
    .insert(v.palletIds.map(pallet_id => ({ lot_id: lotId, pallet_id }))), 'ColdTreatment: lot pallets insert')
  if (e2) throw new Error('The lot was started but its pallets were not added. Open the lot and tap Add pallets. ' + e2.message)
  return lotId
}

// ---------------------------------------------------------------- list
function StatusChip({ q }: { q: { data?: ColdTreatmentStatus; isError: boolean; isPending: boolean } }) {
  if (q.isError) return <span className="text-caption text-warning">Could not check</span>
  if (q.isPending || !q.data) return <span className="text-caption text-fg-muted">Checking…</span>
  return q.data.ready
    ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-900/30 text-success text-caption font-semibold px-2 py-0.5"><CheckCircle2 size={12} aria-hidden="true" /> Ready</span>
    : <span className="inline-flex items-center rounded-full bg-amber-900/30 text-warning text-caption font-semibold px-2 py-0.5">Not ready</span>
}

function LotList({ lots, rooms, onOpen }: { lots: ColdTreatmentLot[]; rooms: CtRoom[]; onOpen: (id: string) => void }) {
  const shown = lots.slice(0, 25)
  const statuses = useQueries({ queries: shown.map(l => ({ queryKey: CT_KEYS.status(l.lot_id), queryFn: () => fetchCtStatus(l.lot_id), staleTime: 30_000 })) })
  const roomBy = new Map(rooms.map(r => [r.coldroom_id, r]))
  return (
    <ul className="space-y-1.5">
      {shown.map((l, i) => (
        <li key={l.lot_id}>
          <button type="button" onClick={() => onOpen(l.lot_id)} className="card w-full text-left px-3 py-2 min-h-[44px] flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="text-sm font-bold text-fg">{l.lot_no}</span>
            <span className="text-caption text-fg-secondary">{roomBy.get(l.coldroom_id)?.code ?? '-'}</span>
            <span className="text-caption text-fg-muted tabular-nums">at or below {fmtC(l.threshold_c)} °C · {l.required_hours} h</span>
            <span className="text-caption text-fg-muted tabular-nums">started {stamp(l.started_at)}</span>
            <span className="ml-auto"><StatusChip q={statuses[i]!} /></span>
          </button>
        </li>
      ))}
      {lots.length > shown.length && <li className="text-caption text-fg-muted">Showing the newest {shown.length} of {lots.length} lots.</li>}
    </ul>
  )
}

// ---------------------------------------------------------------- lot view
function StatusCard({ q }: { q: ReturnType<typeof useCtStatus> }) {
  if (readState(q) !== 'usable' || !q.data) return <QueryStateGate query={q} noun="the lot status" empty={null} />
  const s = q.data
  return (
    <section aria-label="Lot status" className="card p-3 space-y-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-sm font-bold text-fg mr-auto">{s.ready ? 'Ready' : 'Not ready'}</h2>
        <span className="text-caption text-fg-muted tabular-nums">{Math.floor(s.elapsed_hours)} h of {s.required_hours} h · last {s.final_hours} h at or below {fmtC(s.threshold_c)} °C</span>
      </div>
      {s.ready
        ? <p className="text-caption text-success">The treatment finished {s.ready_at ? stamp(s.ready_at) : ''}: every probe was at or below the threshold for the last {s.final_hours} h before that, and no hour was missed. Ready stays on after the probes come out.</p>
        : <ul className="list-disc pl-5 text-caption text-warning space-y-0.5">{s.reasons.map(r => <li key={r}>{r}</li>)}</ul>}
    </section>
  )
}

function MissedBadge({ st }: { st: ColdTreatmentProbeStatus | undefined }) {
  const n = st?.missed_hours ?? 0
  if (n <= 0) return null
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-amber-900/30 text-warning text-caption font-semibold px-2 py-0.5">
      <AlertTriangle size={12} aria-hidden="true" /> Missed {n} {n === 1 ? 'hour' : 'hours'}{(st?.window_missed ?? 0) > 0 ? ' · in the final window' : ''}
    </span>
  )
}

function ProbeLive({ probe, st, canEdit, onRetire }: {
  probe: ColdTreatmentProbe; st: ColdTreatmentProbeStatus | undefined; canEdit: boolean; onRetire: () => void
}) {
  return (
    <>
      {st?.last_pulp_c != null && <span className="text-caption tabular-nums text-fg-secondary">last {fmtC(st.last_pulp_c)} °C at {sastHM(st.last_read_at)}</span>}
      <MissedBadge st={st} />
      {canEdit && (
        <button type="button" onClick={onRetire} aria-label={`Retire probe ${probe.probe_no}`}
          className="ml-auto text-caption text-cyan-400 hover:text-cyan-300 min-h-[44px] px-2.5 rounded-lg hover:bg-cyan-900/20">Retire</button>
      )}
    </>
  )
}

function ProbeCard({ probe, st, canEdit, onRetire, label, retiredBy }: {
  label: string; retiredBy: string | undefined
  probe: ColdTreatmentProbe; st: ColdTreatmentProbeStatus | undefined; canEdit: boolean; onRetire: () => void
}) {
  return (
    <li className={clsx('card px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-1', probe.retired_at && 'opacity-60')}>
      <span className="text-sm font-bold text-fg">Probe {probe.probe_no}</span>
      <span className="text-caption text-fg-secondary">{probe.pallet_id} · {probe.position}</span>
      <span className="text-caption text-fg-secondary">{label}</span>
      <span className="text-caption text-fg-muted">calibrated to {probe.calibration_due_on}</span>
      {probe.retired_at
        ? <span className="text-caption text-fg-muted">Retired: {probe.retired_reason}{retiredBy ? ` (by ${retiredBy})` : ''}</span>
        : <ProbeLive probe={probe} st={st} canEdit={canEdit} onRetire={onRetire} />}
    </li>
  )
}

function QuickEntry({ live, canEdit, isPending, onSave }: {
  live: ColdTreatmentProbe[]; canEdit: boolean; isPending: boolean
  onSave: (rows: { probeId: string; pulp: number }[], done: () => void) => void
}) {
  const [vals, setVals] = useState<Record<string, string>>({})
  const [err, setErr] = useState<string | null>(null)
  if (!canEdit) return <p className="text-caption text-fg-muted">You can read this lot. Recording needs Coldroom edit access: ask your manager.</p>
  if (live.length === 0) return <p className="text-caption text-fg-muted">Place a probe to start reading.</p>
  function submit() {
    const rows: { probeId: string; pulp: number }[] = []
    for (const p of live) {
      const raw = (vals[p.probe_id] ?? '').trim()
      if (raw === '') continue
      const n = parsePulp(raw)
      if (n === null) { setErr(`Probe ${p.probe_no}: type the pulp temperature in °C, for example -0.7.`); return }
      rows.push({ probeId: p.probe_id, pulp: n })
    }
    if (rows.length === 0) { setErr('Type at least one reading.'); return }
    setErr(null); onSave(rows, () => setVals({}))
  }
  return (
    <form className="space-y-2" onSubmit={e => { e.preventDefault(); submit() }}>
      <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(8.5rem, 1fr))' }}>
        {live.map(p => (
          <label key={p.probe_id} className="block">
            <span className="text-caption text-fg-muted">Probe {p.probe_no} pulp °C</span>
            <input value={vals[p.probe_id] ?? ''} inputMode="decimal" placeholder="-0.7"
              onChange={e => setVals(v => ({ ...v, [p.probe_id]: sanitizeTempInput(e.target.value) }))}
              className="field text-base min-h-[44px] mt-0.5 tabular-nums" />
          </label>
        ))}
      </div>
      {err && <p role="alert" className="text-caption text-danger">{err}</p>}
      <Button type="submit" variant="primary" disabled={isPending} className="min-h-[44px] w-full">{isPending ? 'Saving…' : 'Save readings'}</Button>
    </form>
  )
}

function ReadingTags({ r, old }: { r: ColdTreatmentReading; old: boolean }) {
  return (
    <>
      {r.supersedes_id && <span className="text-fg-muted">Correction</span>}
      {old && <span className="text-fg-muted">Corrected</span>}
      {r.note && !old && <span className="text-fg-muted truncate">{r.note}</span>}
    </>
  )
}

function CorrectButton({ r, probeNo, onCorrect }: { r: ColdTreatmentReading; probeNo: number | undefined; onCorrect: (r: ColdTreatmentReading) => void }) {
  return (
    <button type="button" onClick={() => onCorrect(r)} aria-label={`Correct the ${sastHM(r.read_at)} reading of probe ${probeNo ?? ''}`}
      className="ml-auto text-cyan-400 hover:text-cyan-300 min-h-[44px] px-2.5 rounded-lg hover:bg-cyan-900/20">Correct</button>
  )
}

function ReadingRow({ r, old, probeNo, canEdit, onCorrect }: {
  r: ColdTreatmentReading; old: boolean; probeNo: number | undefined; canEdit: boolean; onCorrect: (r: ColdTreatmentReading) => void
}) {
  return (
    <li className="card px-3 py-1.5 flex flex-wrap items-center gap-x-3 text-caption">
      <span className="tabular-nums text-fg-muted">{stamp(r.read_at)}</span>
      <span className="text-fg-secondary">Probe {probeNo ?? '-'}</span>
      <span className={clsx('font-semibold tabular-nums', old ? 'line-through text-fg-muted' : 'text-fg')}>{fmtC(r.pulp_temp_c)} °C</span>
      <ReadingTags r={r} old={old} />
      {canEdit && !old && <CorrectButton r={r} probeNo={probeNo} onCorrect={onCorrect} />}
    </li>
  )
}

function ReadingsList({ readings, probes, canEdit, onCorrect }: {
  readings: ColdTreatmentReading[]; probes: ColdTreatmentProbe[]; canEdit: boolean; onCorrect: (r: ColdTreatmentReading) => void
}) {
  const gone = useMemo(() => supersededIds(readings), [readings])
  const no = new Map(probes.map(p => [p.probe_id, p.probe_no]))
  if (readings.length === 0) return <p className="text-caption text-fg-muted">No readings yet.</p>
  return (
    <ul className="space-y-1">
      {readings.slice(0, 60).map(r => <ReadingRow key={r.reading_id} r={r} old={gone.has(r.reading_id)} probeNo={no.get(r.probe_id)} canEdit={canEdit} onCorrect={onCorrect} />)}
      {readings.length > 60 && <li className="text-caption text-fg-muted">Showing the newest 60 of {readings.length}. The printed record has all of them.</li>}
    </ul>
  )
}

type Dialog = null | { kind: 'pallets' } | { kind: 'probe' } | { kind: 'correct'; r: ColdTreatmentReading } | { kind: 'retire'; p: ColdTreatmentProbe }

const ctJobs = {
  readings: (lotId: string, rows: { probeId: string; pulp: number }[]): Job => async () => {
    const { error } = await withTimeout(supabase.from('cold_treatment_readings')
      .insert(rows.map(r => ({ lot_id: lotId, probe_id: r.probeId, pulp_temp_c: r.pulp }))), 'ColdTreatment: readings insert')
    if (error) throw error
  },
  pallets: (lotId: string, ids: string[]): Job => async () => {
    const { error } = await withTimeout(supabase.from('cold_treatment_lot_pallets')
      .insert(ids.map(pallet_id => ({ lot_id: lotId, pallet_id }))), 'ColdTreatment: lot pallets insert')
    if (error) throw error
  },
  probe: (lotId: string, v: PlaceProbeVars): Job => async () => {
    const { error } = await withTimeout(supabase.from('cold_treatment_probes')
      .insert({ lot_id: lotId, equipment_id: v.equipmentId, pallet_id: v.palletId, position: v.position }), 'ColdTreatment: probe insert')
    if (error) throw error
  },
  correction: (lotId: string, old: ColdTreatmentReading, v: { pulp: number; note: string | null }): Job => async () => {
    const { error } = await withTimeout(supabase.from('cold_treatment_readings').insert({
      lot_id: lotId, probe_id: old.probe_id, pulp_temp_c: v.pulp, note: v.note, supersedes_id: old.reading_id,
    }), 'ColdTreatment: correction insert')
    if (error) throw error
  },
  retire: (probeId: string, reason: string): Job => async () => {
    const { error } = await withTimeout(supabase.from('cold_treatment_probes')
      .update({ retired_at: new Date().toISOString(), retired_reason: reason }).eq('probe_id', probeId), 'ColdTreatment: probe retire')
    if (error) throw error
  },
}

function LotDialogs({ dialog, lot, d, live, write, close }: {
  dialog: Dialog; lot: ColdTreatmentLot; d: LotDetail; live: ColdTreatmentProbe[]; write: ReturnType<typeof useCtWrite>; close: () => void
}) {
  if (!dialog) return null
  const run = (job: Job) => write.mutate(job, { onSuccess: close })
  const common = { isPending: write.isPending, onClose: close }
  switch (dialog.kind) {
    case 'pallets':
      return <AddPalletsModal roomId={lot.coldroom_id} already={d.pallets} {...common} onSave={ids => run(ctJobs.pallets(lot.lot_id, ids))} />
    case 'probe':
      return <PlaceProbeModal pallets={d.pallets} usedEquipment={new Set(live.map(p => p.equipment_id))} {...common} onSave={(v: PlaceProbeVars) => run(ctJobs.probe(lot.lot_id, v))} />
    case 'correct':
      return <CorrectReadingModal reading={dialog.r} probe={d.probes.find(p => p.probe_id === dialog.r.probe_id)} {...common} onSave={v => run(ctJobs.correction(lot.lot_id, dialog.r, v))} />
    case 'retire':
      return <RetireProbeModal probe={dialog.p} {...common} onSave={reason => run(ctJobs.retire(dialog.p.probe_id, reason))} />
  }
}

function PalletsSection({ pallets, canEdit, onAdd }: { pallets: string[]; canEdit: boolean; onAdd: () => void }) {
  return (
    <section aria-label="Pallets" className="space-y-2">
      <div className="flex items-center gap-2">
        <h2 className="text-sm font-bold text-fg mr-auto">Pallets ({pallets.length})</h2>
        {canEdit && <Button variant="ghost" size="sm" className="min-h-[44px]" onClick={onAdd}><Plus size={14} /> Add pallets</Button>}
      </div>
      <p className="text-caption text-fg-secondary break-words">{pallets.length ? pallets.join(', ') : 'No pallets in this lot yet.'}</p>
    </section>
  )
}

function ProbesSection({ d, stBy, live, minProbes, canEdit, onPlace, onRetire }: {
  d: LotDetail; stBy: Map<string, ColdTreatmentProbeStatus>; live: ColdTreatmentProbe[]; minProbes: number; canEdit: boolean
  onPlace: () => void; onRetire: (p: ColdTreatmentProbe) => void
}) {
  const label = useCtProbeLabels(d.probes)
  const people = useMemo(() => d.probes.flatMap(p => (p.retired_by ? [{ recorded_by: p.retired_by }] : [])), [d.probes])
  const names = useRecorderNames(people)
  return (
    <section aria-label="Probes" className="space-y-2">
      <div className="flex items-center gap-2">
        <h2 className="text-sm font-bold text-fg mr-auto">Probes ({live.length} of at least {minProbes})</h2>
        {canEdit && <Button variant="ghost" size="sm" className="min-h-[44px]" disabled={d.pallets.length === 0} onClick={onPlace}><Plus size={14} /> Place a probe</Button>}
      </div>
      {d.probes.length === 0 ? <p className="text-caption text-fg-muted">No probes placed yet.</p> : (
        <ul className="space-y-1.5">{d.probes.map(p => <ProbeCard key={p.probe_id} probe={p} st={stBy.get(p.probe_id)} canEdit={canEdit} onRetire={() => onRetire(p)} label={label(p.equipment_id)} retiredBy={p.retired_by ? names.get(p.retired_by) : undefined} />)}</ul>
      )}
    </section>
  )
}

function LotHeader({ lot, room }: { lot: ColdTreatmentLot; room: CtRoom | undefined }) {
  return (
    <div className="card px-3 py-2">
      <p className="text-sm font-bold text-fg">{lot.lot_no} · {room?.code ?? '-'}{room && !room.usda_approved ? ' · room not marked USDA-approved' : ''}</p>
      <p className="text-caption text-fg-muted tabular-nums">At or below {fmtC(lot.threshold_c)} °C · {lot.required_hours} h in all · last {lot.final_hours} h · started {stamp(lot.started_at)}</p>
      {lot.note && <p className="text-caption text-fg-secondary mt-0.5">{lot.note}</p>}
    </div>
  )
}

function ReadingSections({ lot, d, live, canEdit, write, onCorrect }: {
  lot: ColdTreatmentLot; d: LotDetail; live: ColdTreatmentProbe[]; canEdit: boolean; write: ReturnType<typeof useCtWrite>; onCorrect: (r: ColdTreatmentReading) => void
}) {
  return (
    <>
      <section aria-label="Hourly readings" className="card p-3 space-y-2">
        <h2 className="text-sm font-bold text-fg">Record the hourly readings</h2>
        <QuickEntry live={live} canEdit={canEdit} isPending={write.isPending}
          onSave={(rows, done) => write.mutate(ctJobs.readings(lot.lot_id, rows), { onSuccess: done })} />
      </section>
      <section aria-label="Readings" className="space-y-2">
        <h2 className="text-sm font-bold text-fg">Readings</h2>
        <ReadingsList readings={d.readings} probes={d.probes} canEdit={canEdit} onCorrect={onCorrect} />
      </section>
    </>
  )
}

function LotView({ lot, room, canEdit, write }: {
  lot: ColdTreatmentLot; room: CtRoom | undefined; canEdit: boolean; write: ReturnType<typeof useCtWrite>
}) {
  const statusQ = useCtStatus(lot.lot_id)
  const detailQ = useCtLotDetail(lot.lot_id)
  const [dialog, setDialog] = useState<Dialog>(null)
  const close = () => setDialog(null)
  const d = detailQ.data
  const live = useMemo(() => (d?.probes ?? []).filter(p => !p.retired_at), [d])
  const stBy = new Map((statusQ.data?.probes ?? []).map(p => [p.probe_id, p]))

  return (
    <div className="space-y-4">
      <LotHeader lot={lot} room={room} />
      <StatusCard q={statusQ} />
      {readState(detailQ) !== 'usable' || !d ? <QueryStateGate query={detailQ} noun="the lot record" empty={null} /> : (
        <>
          <PalletsSection pallets={d.pallets} canEdit={canEdit} onAdd={() => setDialog({ kind: 'pallets' })} />
          <ProbesSection d={d} stBy={stBy} live={live} minProbes={statusQ.data?.min_probes ?? 3} canEdit={canEdit}
            onPlace={() => setDialog({ kind: 'probe' })} onRetire={p => setDialog({ kind: 'retire', p })} />
          <ReadingSections lot={lot} d={d} live={live} canEdit={canEdit} write={write} onCorrect={r => setDialog({ kind: 'correct', r })} />
          <LotDialogs dialog={dialog} lot={lot} d={d} live={live} write={write} close={close} />
        </>
      )}
    </div>
  )
}

const HEADER_LINK = 'shrink-0 flex items-center gap-1.5 min-h-[44px] rounded-lg bg-surface hover:bg-raised text-fg text-caption font-semibold px-3'

function HeaderAction({ lot, canEdit, disabled, onStart }: { lot: ColdTreatmentLot | undefined; canEdit: boolean; disabled: boolean; onStart: () => void }) {
  if (lot) return <Link to={`/coldroom/cold-treatment/log?lot=${lot.lot_id}`} className={HEADER_LINK}><Printer size={14} /> Print record</Link>
  if (!canEdit) return null
  return <button type="button" onClick={onStart} disabled={disabled} className={HEADER_LINK}><Plus size={14} /> Start a lot</button>
}

function pickBody({ failed, lotId, lot, lots, rooms, canEdit, write, setLotId }: {
  failed: UseQueryResult<unknown> | null; lotId: string; lot: ColdTreatmentLot | undefined; lots: ColdTreatmentLot[]; rooms: CtRoom[]
  canEdit: boolean; write: ReturnType<typeof useCtWrite>; setLotId: (id: string) => void
}): ReactNode {
  if (failed) return <QueryStateGate query={failed} noun="cold treatment lots" empty={null} />
  if (lotId && !lot) return <p className="text-caption text-fg-muted">That lot was not found. <button type="button" className="text-cyan-400 min-h-[44px]" onClick={() => setLotId('')}>Back to the lots</button></p>
  if (lot) return <LotView lot={lot} room={rooms.find(r => r.coldroom_id === lot.coldroom_id)} canEdit={canEdit} write={write} />
  if (lots.length === 0) return <p className="text-caption text-fg-muted">No cold treatment lot has been started yet.{canEdit ? ' Tap Start a lot.' : ''}</p>
  return <LotList lots={lots} rooms={rooms} onOpen={setLotId} />
}

// ---------------------------------------------------------------- page
export default function ColdTreatment({ tabs }: { tabs: ReactNode }) {
  const { can } = usePermissions()
  const canEdit = can('coldroom', 'edit')
  const [lotId, setLotId] = useUrlState<string>('lot', '', s => s, { elide: true })
  const [starting, setStarting] = useState(false)
  const lotsQ = useQuery({ queryKey: CT_KEYS.lots, queryFn: fetchCtLots, refetchInterval: 60_000 })
  const roomsQ = useQuery({ queryKey: CT_KEYS.rooms, queryFn: fetchCtRooms })
  const write = useCtWrite()
  const rooms = useMemo(() => roomsQ.data ?? [], [roomsQ.data])
  const lots = useMemo(() => lotsQ.data ?? [], [lotsQ.data])
  const lot = lots.find(l => l.lot_id === lotId)
  const failed = readState(lotsQ) !== 'usable' ? lotsQ : readState(roomsQ) !== 'usable' ? roomsQ : null

  const body = pickBody({ failed, lotId, lot, lots, rooms, canEdit, write, setLotId })

  return (
    <div className="min-h-full bg-canvas flex flex-col">
      <PageHeader title="Coldroom" icon={<Snowflake size={18} />} subtitle="Cold treatment (USA)"
        actions={<HeaderAction lot={lot} canEdit={canEdit} disabled={!!failed} onStart={() => setStarting(true)} />} />
      {tabs}
      <SubmitOverlay isPending={write.isPending} isSuccess={write.isSuccess} isError={write.isError} error={write.error}
        domain={COLD_TREATMENT_DOMAIN} successLabel="Saved" onDismiss={() => write.reset()} />
      <div className="p-4 space-y-3 max-w-3xl w-full mx-auto">
        {lot && <button type="button" onClick={() => setLotId('')} className="text-caption text-cyan-400 min-h-[44px]">All lots</button>}
        {body}
      </div>
      {starting && (
        <StartLotModal rooms={rooms} isPending={write.isPending} onClose={() => setStarting(false)}
          onSave={v => write.mutate(async () => { const id = await startLot(v); setLotId(id); setStarting(false) })} />
      )}
    </div>
  )
}
